import { ITag } from "@app/interfaces/ITag";
import { IAutocompleteMatch } from "@directives/_forms/autocomplete/autocomplete.component";
import { IPhoneNumberOutput } from "@directives/_forms/smsInput/smsInput.interfaces";
import { IGeofenceNavigationPoint } from "@directives/_map/geofences/geofences.interfaces";
import { IVeriMapComponent, MapPopupType } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { GeofenceType } from "@enums/GeofenceType";
import { AccountUserType } from "@enums/UserType";
import { GeofenceCategory, IGeofence } from "@interfaces/IGeofences";
import { IGoogleMapsMarker } from "@interfaces/IGoogleMapsMarkerOptions";
import { IPlot } from "@interfaces/IPlot";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { FileReaderService } from "@services/fileReader.service";
import { GeofenceService, IGeofenceShapeValidation } from "@services/geofence.service";
import { JourneyService } from "@services/journey.service";
import { MessageDialogService } from "@services/messageDialog.service";
import { TranslationService } from "@services/translation.service";
import { ValidationService } from "@services/validation.service";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { CoordsModel } from "../../../livemap/model/CoordsModel";
import './geofenceEditor.component.scss';

interface IComponentBindings {
    geofence: IGeofence;
    resource: IVehicle | IPlot;
    focusedLatLngs?: google.maps.LatLng;
    onGeofenceEditorCancelled: () => void;
    onGeofenceEditorDeleted: (args: { geofence: IGeofence }) => void;
    onGeofenceEditorSaved: (args: { geofence: IGeofence }) => void;
}

class GeofenceEditorComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    geofence: IGeofence;
    resource: IVehicle | IPlot;
    focusedLatLngs?: google.maps.LatLng;
    onGeofenceEditorCancelled: () => void;
    onGeofenceEditorDeleted: (args: { geofence: IGeofence }) => void;
    onGeofenceEditorSaved: (args: { geofence: IGeofence }) => void;

    // form changes
    geofenceForm: angular.IFormController;
    dirtyGeofence: IGeofence;
    geofenceFacade: IGeofence;

    // spinners
    geofenceInitialized: boolean = false;
    isSavingGeofence: boolean = false;
    isDeletingGeofence: boolean = false;

    // map
    showMap: boolean = false;
    veriMap: IVeriMapComponent;
    navigationPointEditorEnabled: boolean = false;
    showNavigationPointEditor: boolean = false;
    selectedNavigationPoint: IGeofenceNavigationPoint;

    // other
    wizardMode: boolean = false;
    currentTabValidationError: string;
    GeofenceCategory = GeofenceCategory;
    currentTabIndex: number;
    currentTabTitle: string;
    journeyEndPoints: IGoogleMapsMarker<IPlot>[] = [];
    currentUser: IUser;
    geoJsonInputElement: HTMLElement;
    geoJsonPoints: [number, number][];

    // navigation point form
    newNavigationPointName: string;

    showResource: boolean = false;
    showResourceToggle: boolean = false;
    showIgnitionOff: boolean = false;
    loading: boolean = false;

    // validation
    geofenceValidation: IGeofenceShapeValidation;
    providedEmailIsValid: boolean = true;
    isPhoneNumberValid: boolean;
    canSetRestrictedFlag:boolean = true

    popupPosition: google.maps.LatLng;

    get ignoreGeofenceMaxSize() {
        return this.dirtyGeofence?.Category == GeofenceCategory.Zone;
    }

    static $inject = ["$q", "$mdDialog", "$timeout","$rootScope", "geofenceService", "journeyService", "messageDialogService", "translationService", "validationService", "fileReaderService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly geofenceService: GeofenceService,
        private readonly journeyService: JourneyService,
        private readonly messageDialogService: MessageDialogService,
        private readonly translationService: TranslationService,
        private readonly validationService: ValidationService,
        private readonly fileReaderService: FileReaderService,
    ) {
        this.currentUser = angular.copy($rootScope.currentUser);
    }

    $onInit() {
        this.$timeout(() => {   
            this.showMap = true;
            this.geoJsonInputElement = document.getElementById('geoJsonFile');
        }, 300);

        if (this.resource) {
            this.showResource = true;
            this.showResourceToggle = true;
        }

        this.navigationPointEditorEnabled = true;

        if (!this.wizardMode && this.geofence) {
            this.currentTabTitle = this.translationService.t(`geofenceEditor.addressOtherDetails`);
            this.prepareGeofence(this.geofence);
        } else {
            this.currentTabTitle = this.translationService.t(`geofenceEditor.locationShape`);
            this.prepareWizardMode();
        }
    }

    onVeriMapLoaded(veriMap: IVeriMapComponent): void {
        this.veriMap = veriMap;

        this.refreshMap(this.veriMap);
    }

    onTabChanged(tabIndex: number, tabTitle: string): void {
        this.geofenceFacade = {
            ...this.dirtyGeofence,
            Description: (this.dirtyGeofence?.Id > 0) ? this.dirtyGeofence?.Description : this.translationService.t("geofenceEditor.NewGeofence")
        };

        this.currentTabIndex = tabIndex;
        this.currentTabTitle = tabTitle;

        this.refreshValidationErrors();
    }

    onTagDeleted(tag: ITag): void {
        const deletedTag = copy(tag);

        if (!this.dirtyGeofence) {
            return;
        }

        this.dirtyGeofence.Tags = this.geofence.Tags.filter(tag => tag.Id != deletedTag.Id);
        this.dirtyGeofence.TagIds = this.geofence.TagIds.filter(id => id != deletedTag.Id);
    }

    cancel(): void {
        if (this.onGeofenceEditorCancelled) {
            this.onGeofenceEditorCancelled();
        }
    }

    deleteGeofence(geofence: IGeofence): void {
        if (!geofence || this.isDeletingGeofence) {
            return;
        }

        this.isDeletingGeofence = true;

        this.geofenceService
            .deleteGeofence(geofence)
            .then(() => {
                this.isDeletingGeofence = false;

                if (this.onGeofenceEditorDeleted) {
                    this.onGeofenceEditorDeleted({ geofence });
                }
            })
            .catch(err => {
                this.messageDialogService.handleError(err, this.translationService.t("geofenceEditor.UnableToDelete"))
            })
            .finally(() => {
                this.isDeletingGeofence = false;
            });
    }

    onToggleResource(show: boolean): void {
        this.showResource = show;
    }

    showIgnitionOffToggle(show: boolean): void {
        this.showIgnitionOff = show;
    }

    getRecentIgnitionOffs(geofence: IGeofence) {
        if (!geofence) {
            return;
        }

        this.loading = true;

        this.journeyService.getAllJourneys(moment().subtract(1, 'week').toDate(), moment().toDate())
            .then(journeys => {

                if (this.dirtyGeofence?.GeofenceType == GeofenceType.Polygon) {
                    this.setPolygonGeofenceRadius();
                }

                const ignitionRadius = new google.maps.Circle({
                    radius: this.dirtyGeofence ? this.dirtyGeofence.Radius * 2 : 1200,
                    center: this.focusedLatLngs ? this.focusedLatLngs : new google.maps.LatLng(this.dirtyGeofence.Latitude, this.dirtyGeofence.Longitude)
                });

                journeys?.forEach(journey => {
                    const journeyEndPlot: IGoogleMapsMarker<IPlot> = {
                        id: journey.Id,
                        options: {
                            position: { lat: journey.EndLatitude, lng: journey.EndLongitude },
                            icon: {
                                path: google.maps.SymbolPath.CIRCLE,
                                scale: 6,
                                fillColor: "#152238",
                                fillOpacity: 1,
                                strokeWeight: 2,
                                strokeOpacity: 0.8,
                                strokeColor: 'white',
                            },
                            title: this.translationService.t(`geofenceEditor.ignitionOff`)
                        }
                    };

                    var distance = google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(journeyEndPlot.options.position), this.focusedLatLngs ? this.focusedLatLngs : new google.maps.LatLng(this.geofence.Latitude, this.geofence.Longitude));

                    if (distance < ignitionRadius.getRadius() && !this.journeyEndPoints.find(ep => ep.options.position == journeyEndPlot.options.position)) {
                        this.journeyEndPoints.push(journeyEndPlot);
                    }
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    saveGeofence($event: MouseEvent, geofence: IGeofence): void {
        if (!geofence || this.isSavingGeofence) {
            return;
        }

        this.isSavingGeofence = true;
        const isNewGeofence: boolean = !geofence?.Id;

        this.dirtyGeofence.Category = +this.dirtyGeofence.Category;
        this.cleanupGeofencePointsAndRadius(geofence);

        this.geofenceService
            .saveGeofence(geofence)
            .then((savedGeofenceId: number) => {
                geofence.Id = savedGeofenceId;

                this.applyGeofenceIdToNavigationPoints(geofence.Id, this.dirtyGeofence?.navigationPoints);

                this.geofenceService.saveGeofenceNavigationPoints(geofence.Id, this.dirtyGeofence?.navigationPoints).finally(() => {
                    this.isSavingGeofence = false;

                    if (this.onGeofenceEditorSaved) {
                        this.onGeofenceEditorSaved({ geofence: this.dirtyGeofence });
                    }
                });

            }).catch((err) => {
                var title = isNewGeofence ? this.translationService.t('geofenceEditor.unableToCreate') : this.translationService.t('geofenceEditor.unableToSave');
                this.messageDialogService.handleError(err, title, $event);
            });
    }

    applyGeofenceIdToNavigationPoints(geofenceId: number, navigationPoints: IGeofenceNavigationPoint[] = []): void {
        navigationPoints?.forEach(navigationPoint => {
            navigationPoint.GeofenceId = geofenceId;
        });
    }

    removeNavigationPoint(navigationPoint: IGeofenceNavigationPoint): void {
        if (!navigationPoint) {
            return;
        }

        this.dirtyGeofence.navigationPoints = [...this.dirtyGeofence.navigationPoints.filter(navPoint => navPoint !== navigationPoint)];

        this.closeNavigationPointEditor();
    }

    openNavigationPointEditor($event: MouseEvent, navigationPoint: IGeofenceNavigationPoint = null): void {
        $event?.stopPropagation();

        this.selectedNavigationPoint = navigationPoint;

        if (navigationPoint) {
            this.newNavigationPointName = navigationPoint?.Name;
        }

        this.showNavigationPointEditor = true;
    }

    closeNavigationPointEditor(): void {
        this.selectedNavigationPoint = null;
        this.showNavigationPointEditor = false;
        this.clearNewNavigationPointName();
    }

    addNavigationPoint(): void {
        if (!this.newNavigationPointName) {
            return;
        }

        const latLngs: google.maps.LatLng = this.veriMap?.getMapOffsetCenter();

        if (latLngs) {
            this.dirtyGeofence.navigationPoints = (this.dirtyGeofence.navigationPoints) ? this.dirtyGeofence.navigationPoints : [];
            this.dirtyGeofence.navigationPoints.push({
                Id: -1,
                Name: `${this.newNavigationPointName}`,
                Latitude: latLngs.lat(),
                Longitude: latLngs.lng(),

                //--
                coords: new CoordsModel(latLngs.lat(), latLngs.lng())
            });

            this.dirtyGeofence.navigationPoints = [...this.dirtyGeofence.navigationPoints];
        }

        this.closeNavigationPointEditor();
    }

    saveNavigationPoint(): void {
        if (!this.newNavigationPointName || !this.selectedNavigationPoint) {
            return;
        }

        this.selectedNavigationPoint.Name = this.newNavigationPointName;
        this.dirtyGeofence.navigationPoints = [...this.dirtyGeofence.navigationPoints];

        this.closeNavigationPointEditor();
    }

    updateNavigationPointPosition(navigationPoint: IGeofenceNavigationPoint = null, position: google.maps.LatLng = null): void {
        if (!this.dirtyGeofence || !navigationPoint || !position) {
            return;
        }

        if (navigationPoint) {
            navigationPoint.Latitude = position.lat();
            navigationPoint.Longitude = position.lng();
        }
    }

    onGeofenceCategoryChanged(geofence: IGeofence): void {
        this.dirtyGeofence.GeofenceType = GeofenceType.Polygon;

        this.refreshValidationErrors();
    }

    onLocationSearchResult(match: IAutocompleteMatch): void {
        if (!match) {
            return;
        }

        this.prepareNewGeofenceAtLocation(new google.maps.LatLng(match.location.lat, match.location.lng));
    }

    setGeofencePositionFromPopup(position: google.maps.LatLng): void {
        this.veriMap?.hidePopup(MapPopupType.RightClick, true);

        this.prepareNewGeofenceAtLocation(position);
    }

    onShowRightClickPopup(position: google.maps.LatLng) {
        this.popupPosition = position;
    }

    onFormUpdated(): void {
        this.refreshValidationErrors();
    }

    onPhoneOutputChanged(output: IPhoneNumberOutput): void {
        this.dirtyGeofence.Phone = output.internationalized;
        this.isPhoneNumberValid = output.isValid;

        this.onFormUpdated();
    }

    zoomToNavigationPoint(navigationPoint: IGeofenceNavigationPoint): void {
        if (!navigationPoint?.Latitude || !navigationPoint?.Longitude) {
            return;
        }

        this.veriMap?.fitMap([new google.maps.LatLng(navigationPoint.Latitude, navigationPoint.Longitude)]);
    }

    onKeyupNavPointInput(event: KeyboardEvent): void {
        if (event?.keyCode !== 13) {
            return;
        }

        if (!this.selectedNavigationPoint) {
            this.addNavigationPoint();
        } else {
            this.saveNavigationPoint();
        }
    }

    // ---

    private refreshValidationErrors(): void {
        this.validateGeofenceShape(this.dirtyGeofence);
        this.$timeout(() => {
            this.currentTabValidationError = (!this.geofenceValidation?.valid && this.geofenceValidation?.failedReason)
                ? this.geofenceValidation.failedReason
                : (!this.isFormValid()) ? this.translationService.t('geofenceEditor.completeRequiredFields') : null;
        }, 100);
    }

    private isFormValid(): boolean {
        // ignore validation of form as we're not on the right tab
        if (this.wizardMode && this.currentTabIndex !== 2
            || !this.wizardMode && this.currentTabIndex !== 0) {
            return true;
        }

        let formIsPending = false;
        let formIsValid = false;

        if (this.geofenceForm) {
            formIsValid = this.geofenceForm.$valid;
            formIsPending = !!this.geofenceForm.$pending;
        }

        return formIsValid && !formIsPending && this.isEmailValid(this.dirtyGeofence?.ContactEmail) && this.isPhoneNumberValid;

    }

    private validateGeofenceShape(geofence: IGeofence): void {
        if (!geofence) {
            this.geofenceValidation = { valid: false, failedReason: this.translationService.t('geofenceEditor.confirmLocation'), geofence: null };
            return;
        }

        this.geofenceValidation = this.geofenceService.validateGeofenceShape(geofence, this.ignoreGeofenceMaxSize);
    }

    private isEmailValid(email: string): boolean {
        if (!email || email === "") {
            this.providedEmailIsValid = true;
        } else {
            this.providedEmailIsValid = this.validationService.emailIsValid(email);
        }
        return this.providedEmailIsValid;
    }

    private prepareNewGeofenceAtLocation(position: google.maps.LatLng): void {
        if (!position) {
            return;
        }

        this.focusedLatLngs = position;
        this.veriMap?.fitMap([this.focusedLatLngs]);
        this.prepareGeofence(this.geofence);
    }

    private prepareWizardMode(): void {
        this.wizardMode = true;

        if (!this.focusedLatLngs) {
            this.focusedLatLngs = new google.maps.LatLng(this.veriMap?.getMap()?.getCenter().lat(), this.veriMap?.getMap()?.getCenter().lng());

        } else {
            this.prepareGeofence(this.geofence);
        }
    }

    private cleanupGeofencePointsAndRadius(geofence: IGeofence): void {
        if (!geofence) {
            return;
        }

        geofence.Points = (geofence.GeofenceType === GeofenceType.Circle) ? [] : geofence.Points;
        geofence.Shapes = (geofence.GeofenceType === GeofenceType.Circle) ? [[]] : geofence.Shapes;

        geofence.Radius = (geofence.GeofenceType === GeofenceType.Polygon) ? 0 : geofence.Radius;
        geofence.Shapes = (geofence.GeofenceType === GeofenceType.Polygon) ? [[...geofence.Points]] : geofence.Shapes;
    }

    private clearNewNavigationPointName(): void {
        this.newNavigationPointName = ``;
    }

    private setNavigationPointEditorEnabled(value: boolean | number): void {
        this.navigationPointEditorEnabled = value as boolean;
    }

    private setPolygonGeofenceRadius() {
        let latLngs: google.maps.LatLng[] = [];

        const points = this.geofenceService?.getAllGeofencePoints(this.dirtyGeofence);
        points?.forEach(point => {
            latLngs.push(new google.maps.LatLng(point[0], point[1]));
        })

        let poly = new google.maps.Polygon({
            paths: latLngs
        });

        const path = poly.getPath();

        let area = google.maps.geometry.spherical.computeArea(path.getArray());
        this.dirtyGeofence.Radius = Math.sqrt(area) * 1.3;
    }

    private prepareGeofence(geofence: IGeofence): void {
        this.loading = true;

        if (!geofence) {
            this.geofenceService.createNewGeofence(this.focusedLatLngs, this.resource).then(newGeofence => {
                this.dirtyGeofence = newGeofence;

                if (this.geoJsonPoints) {
                    this.updateWithImportedData();
                }

                this.geofenceService.setGeofenceAddressFromPosition(this.dirtyGeofence).finally(() => {
                    this.prepareGeofence(this.dirtyGeofence);
                });
            });

            return;
        }

        this.dirtyGeofence = copy(geofence);
        this.getRecentIgnitionOffs(this.dirtyGeofence);


        this.canSetRestrictedFlag = this.currentUser.CurrentAccountUserMapping.Type === AccountUserType.Admin && !this.dirtyGeofence.isSystemGeofence;

        this.refreshValidationErrors();
        this.geofenceInitialized = true;
        this.refreshMap(this.veriMap);

        this.loading = false;
    }

    private updateWithImportedData(): void {
        this.dirtyGeofence.Points = this.geoJsonPoints;
        this.dirtyGeofence.GeofenceType = GeofenceType.Polygon;
        this.dirtyGeofence.Category = 1;
        this.dirtyGeofence.isSystemGeofence = true;
    }

    importGeoJson(): void {
        this.geoJsonInputElement?.click();
    }

    readGeoJsonFile(file: File): void {
        this.fileReaderService.readAsText(file).then(geoJson => JSON.parse(geoJson)).then(geoJson => {

            const latLngs = this.geofenceService.getPolygonCenterCoordinates(geoJson);

            if (!latLngs) {
                return;
            }

            this.focusedLatLngs  = new google.maps.LatLng(latLngs);
            this.geoJsonPoints = this.geofenceService.getPolygonPoints(geoJson);
            this.prepareWizardMode();

        }).catch(error => console.log(error))
    }

   

    private refreshMap(veriMap: IVeriMapComponent): void {
        if (!veriMap) {
            return;
        }

        this.geofenceService.fitMapToGeofences(veriMap, this.dirtyGeofence, []);
    }
}

export class GeofenceEditorComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            geofence: "<",
            resource: "<?",
            focusedLatLngs: "<?",
            onGeofenceEditorCancelled: "&",
            onGeofenceEditorDeleted: "&",
            onGeofenceEditorSaved: "&",
        };

        this.controller = GeofenceEditorComponentController;
        this.template = require("./geofenceEditor.component.html");
    }
}
